import React, { Component } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import * as _ from 'lodash';
import moment from 'moment';
import { Close as CloseIcon } from '@material-ui/icons';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import ReportProblemOutlinedIcon from '@material-ui/icons/ReportProblemOutlined';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { withStyles } from '@material-ui/core/styles';
import axios from 'axios';
import { baseURL } from 'utils/apiEndpoints';
import { TIMEOUT } from 'utils/consts';
import { getIndiaDate } from 'utils/common';
import Button from 'UI/button';

class SellPriceModal extends Component {
  constructor(props) {
    super(props);
    const aiItineraryData = _.defaultTo(props.aiItineraryData, {});
    const { sellPrice, tokenAmount, installments } = aiItineraryData;
        
    this.state = {
      sellPriceTemp: _.defaultTo(sellPrice, 0),
      tokenAmountTemp: _.defaultTo(tokenAmount, 0),
      installments: _.defaultTo(installments, []),
      originalInstallments: [],
      checkingTokenAmount: false,
      errorMessage: '',
      commissionPercent: null,
      sendingItinerary: false,
    };
    this.debouncedUpdateInstallments = _.debounce((tokenChangeAllowed) => this.updateInstallments(tokenChangeAllowed), 200);    
  }

  componentDidMount() {
    this.debouncedUpdateInstallments(true);
  }

  componentDidUpdate(prevProps) {
    const { aiItineraryData } = this.props;
    if (!_.isEqual(aiItineraryData.offer, prevProps.aiItineraryData.offer)) {
      this.updateInstallments(true);
    }
  }

  // Calculate derived values
  getCalculatedValues = () => {
    const { aiItineraryData } = this.props;
    const { costPrice, offer } = aiItineraryData;
    const { sellPriceTemp, tokenAmountTemp, commissionPercent } = this.state;
    
    const offerAmount = offer?.offerAmount || 0;
    const finalPrice = sellPriceTemp ? (sellPriceTemp - offerAmount) : '-';
    
    const margin = (typeof finalPrice === 'number' && finalPrice > 0) ? finalPrice - costPrice : 0;
    const earnings = ((margin > 0) && commissionPercent) ? _.round((margin * commissionPercent) / 100) : 0;
    const finalMarkup = margin;
    
    const remainingAmount = (typeof finalPrice === 'number' && finalPrice > 0) ? finalPrice - tokenAmountTemp : 0;
    const suggestedPrice = _.ceil(costPrice/0.88/1000) * 1000;
    
    return {
      margin,
      finalPrice,
      earnings,
      finalMarkup,
      remainingAmount,
      suggestedPrice,
    };
  };

  // Format price with commas
  formatPrice = (price) => {    
    return (price > 0) ? `₹${price.toLocaleString('en-IN')}` : '';
  };

  // Handle input changes
  handleSellingPriceChange = async (value) => {   
    const { aiItineraryData } = this.props;
    const { costPrice } = aiItineraryData;
    const newSellingPrice = value ? _.toNumber(value) : '';
    const fetchingInstallments = newSellingPrice >= costPrice;
    await this.setState({ 
      sellPriceTemp: newSellingPrice,
      fetchingInstallments,
    });
    if (fetchingInstallments) {
      this.debouncedUpdateInstallments(true);
    }
  };

  handleTokenAmountChange = async (e) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    const tokenAmount = value ? _.parseInt(value, 10) : 0;
    const { sellPriceTemp } = this.state;
    
    if (tokenAmount > sellPriceTemp) {
      this.setState({
        errorMessage: 'Token amount cannot be greater than final price',
        tokenAmountTemp: tokenAmount,
      });
      return;
    }
    
    await this.setState({ 
      tokenAmountTemp: tokenAmount,
      checkingTokenAmount: true,
      errorMessage: '',
      fetchingInstallments: true,
    });
    this.debouncedUpdateInstallments(false);
  };

  handleSendItinerary = async () => {
    const {
      openPricingModal, aiItineraryData, itineraryId, updateItineraryData, showSnackbar,
    } = this.props;
    const { sellPriceTemp, tokenAmountTemp } = this.state;
    await this.setState({ sendingItinerary: true });

    try {
      const response = await axios({
        url: `${baseURL}itineraries/${itineraryId}/send`,
        method: 'put',
        data: { 
          sellPrice: sellPriceTemp,
          offer: aiItineraryData.offer,
          tokenAmount: tokenAmountTemp,
        },
        headers: this.headers,
        timeout: TIMEOUT,
      });
      await updateItineraryData(response.data.itinerary);      
      this.setState({ sendingItinerary: false });
      openPricingModal('itinerarySent');      
    } catch (error) {
      this.setState({ sendingItinerary: false });
      showSnackbar('Something went wrong, please contact support', 'error');
    }  
  };

  handleCreateOffer = () => {
    const { sellPriceTemp } = this.state;
    const { openPricingModal, aiItineraryData, updateItineraryData } = this.props;

    updateItineraryData({
      ...aiItineraryData,
      sellPrice: sellPriceTemp,
    });

    if (openPricingModal) {
      openPricingModal('offer');
    }
  };

  // Format offer display name
  getOfferDisplayName = (offer) => {
    if (!offer) return '';
    if (offer.offerName === 'custom') {
      return offer.customOfferName || '';
    }
    return (offer.offerName || '').split('-').map((word) => _.upperCase(word)).join(' ');
  };

  // Format date for display
  formatOfferDate = (dateStr) => {
    if (!dateStr) return '';
    return moment(dateStr).format('Do MMMM YYYY');
  };

  updateInstallments(tokenChangeAllowed) {
    const {
      aiItineraryData,
      itineraryParts,
      itineraryId,
    } = this.props;
    const { tokenAmountTemp, sellPriceTemp } = this.state;

    const servicesCostPrice = _.sumBy(itineraryParts.filter((ip) => ip.type === 'VISA'), (ip) => (ip.manualCostPrice || ip.costPrice) * 100);
    // Create pricing object in the format expected by API (multiply by 100)
    const pricingToSend = {
      totalCostPrice: _.round(_.defaultTo(aiItineraryData.costPrice, 0) * 100),
      totalSellPrice: _.round(_.defaultTo(sellPriceTemp, 0) * 100),
      finalSellPrice: _.round(_.defaultTo(sellPriceTemp, 0) * 100),
      landPackage: {
        costPrice: _.round(_.defaultTo(aiItineraryData.costPrice, 0) * 100 - servicesCostPrice),
        sellPrice: _.round(_.defaultTo(sellPriceTemp, 0) * 100 - servicesCostPrice),
      },
      flights: { costPrice: 0, sellPrice: 0 },
      services: {
        costPrice: _.round(servicesCostPrice),
        sellPrice: _.round(servicesCostPrice),
      },
      offer: {
        offerAmount: _.round(_.defaultTo(aiItineraryData.offer?.offerAmount, 0) * 100),        
        expiryDate: _.defaultTo(aiItineraryData.offer?.expiryDate, ''),
      },
    };
    axios({
      method: 'post',
      url: `${baseURL}itinerary/installments`,
      data: {
        itinerary: {
          _id: itineraryId,
          pricing: pricingToSend,
        },
        earliestTravelDate: _.min((itineraryParts || []).map((ip) => ip.startDate)),
        tokenAmount: tokenChangeAllowed ? 0 : tokenAmountTemp * 100,
      },
      timeout: TIMEOUT,
    })
      .then((resp) => {
        let newTokenAmount = tokenAmountTemp;
        const newState = {
          installments: resp.data?.installments || [],
          originalInstallments: resp.data?.originalInstallments || [],
          checkingTokenAmount: false,
          commissionPercent: resp.data?.commissionPercent,
        };

        if (tokenChangeAllowed) {
          const firstInstallmentAmount = resp.data?.installments?.[0]?.amount || 0;
          newTokenAmount = _.min([pricingToSend.finalSellPrice, _.max([firstInstallmentAmount, tokenAmountTemp * 100])]);
          newState.tokenAmountTemp = newTokenAmount/100;
        }

        const minTokenAmount = tokenChangeAllowed
          ? resp.data?.originalInstallments?.[0]?.amount / 100 || 0
          : resp.data?.installments?.[0]?.amount / 100 || 0;
        if (newTokenAmount < minTokenAmount) {
          newState.errorMessage = `Token amount should not be less than ₹${_.round(minTokenAmount)}`;
        } else {
          newState.errorMessage = '';
        }

        newState.fetchingInstallments = false;
        this.setState(newState);
      })
      .catch((error) => {
        this.setState({ checkingTokenAmount: false });
        console.error('Error updating installments:', error);
      });
  }

  render() {
    const {
      isOpen, closePricingModal, aiItineraryData, classes,
      openPricingModal,
    } = this.props;
    const { costPrice, offer } = aiItineraryData;

    if (!isOpen) return null;

    const { 
      sellPriceTemp, 
      tokenAmountTemp, 
      installments, 
      originalInstallments,
      checkingTokenAmount,
      errorMessage,
      sendingItinerary,
      fetchingInstallments,
    } = this.state;
    const {
      finalPrice, earnings, margin, suggestedPrice,
    } = this.getCalculatedValues();

    const hasOffer = offer?.offerAmount > 0;
    const offerAmount = offer?.offerAmount || 0;
    const offerName = this.getOfferDisplayName(offer);
    const offerExpiry = this.formatOfferDate(offer?.expiryDate);
    const manualCostPricesCount = aiItineraryData.itineraryParts.filter((ip) => ip.manualCostPrice).length;

    return (
      <div
        className={classes.overlay}
        onClick={closePricingModal}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Escape') closePricingModal();
        }}
      >
        <div
          className={classes.modal}
          onClick={(e) => e.stopPropagation()}
          role="button"
          tabIndex={0}
        >
          {/* Header */}
          <div className={classes.header}>
            <div className={classes.headerTitle}>
              <CheckCircleOutlineIcon className={classes.headerIcon} height={20} width={20} />
              Confirm Pricing & Send
            </div>
            <button
              type="button"
              className={classes.closeButton}
              onClick={closePricingModal}
            >
              <CloseIcon />
            </button>
          </div>

          {/* Content */}
          <div className={classes.content}>
            {/* Cost Price Section */}
            <div className={classes.costPriceBox}>
              <div className={classes.costPricePrimary}>
                <div className={classes.costPriceLabel}>Cost Price</div>
                <div className={classes.costPriceValue}>{this.formatPrice(costPrice)}</div>
                <div className={classes.costDescription}>
                  Inclusive of hotels, activities, transfers, visa & cruise
                </div>          
              </div>
              {
                manualCostPricesCount > 0 && (
                  <div className={classes.costPriceSecondary}>
                    <ReportProblemOutlinedIcon className={classes.warningIcon} />                    
                    <div>
                      <div className={classes.summary}>You&apos;ve updated {manualCostPricesCount} item prices</div>
                      <Button
                        variant="outlined"
                        className={classes.editPricingButton}
                        onClick={() => {
                          openPricingModal('cost');
                        }}
                      >
                        Edit Pricing
                      </Button>
                    </div>
                  </div>
                )
              }
            </div>
            
            <div className={classes.sellPriceBox}>
              {/* Left Column */}
              <div className={classes.gridColumn}>
                <div className={classes.displaySection}>
                  <div className={classes.displayLabel}>Set Selling Price</div>
                  <div className={classes.inputContainer}>
                    <span className={classes.currencySymbol}>₹</span>
                    <input
                      type="text"
                      className={clsx(
                        classes.priceInput,
                        sellPriceTemp ? null : classes.placeholder,
                        (sellPriceTemp && sellPriceTemp < costPrice) ? classes.priceInputError : null
                      )}
                      value={(sellPriceTemp || '')}
                      onChange={(e) => this.handleSellingPriceChange(e.target.value.replace(/[^0-9]/g, ''))}
                      placeholder={suggestedPrice.toString()}
                    />
                  </div>
                </div>
                <div className={classes.displaySection}>
                  <div className={classes.displayLabel}>Final Price</div>
                  <div className={classes.displayValue}>
                    {this.formatPrice(finalPrice)}
                  </div>
                </div>

                <div className={classes.displaySection}>
                  <div className={classes.displayLabel}>Margin</div>
                  <div className={classes.displayValue} style={{ fontWeight: 100 }}>
                    {margin > 0 ? this.formatPrice(margin) : '--'}
                  </div>
                </div>

                <div className={classes.displaySection}>
                  <div className={classes.displayLabel}>Your Earnings</div>
                  <div className={classes.earningsValue}>
                    {(earnings > 0) ? this.formatPrice(earnings) : '--'}
                  </div>
                </div>
              </div>
              <div className={classes.suggestedPrice}>
                <InfoOutlinedIcon className={classes.infoIcon} height={12} width={12} />
                <b>{this.formatPrice(suggestedPrice)}</b> suggested price
              </div>
              <div className={classes.gridColumn}>
                {/* Create Offer Section */}
                <div className={classes.offerSection}>
                  <span className={classes.offerIcon} role="img" aria-label="price tag">🏷️</span>
                  <div className={classes.offerText}>
                    {hasOffer ? (
                      <>
                        <strong>{this.formatPrice(offerAmount)}</strong>
                        {' discount applied'}
                        {offerName && <> ({offerName})</>}
                        {', valid till '}
                        <strong>{offerExpiry}</strong>
                      </>
                    ) : (
                      'Push clients to book soon with limited time offers'
                    )}
                  </div>
                  <button
                    type="button"
                    className={classes.createOfferButton}
                    onClick={this.handleCreateOffer}
                  >
                    {hasOffer ? 'Edit Offer' : 'Create Offer'}
                  </button>
                </div>
              </div>
            </div>

            {/* Token Amount Section */}
            <div className={classes.divider} />
            <div className={classes.tokenAmountSection}>              
              <div className={classes.sectionLabel}>
                Set Token Amount {checkingTokenAmount ? '...' : ''}
              </div>
              <div className={classes.tokenAmountRow}>
                <div className={classes.tokenInputContainer}>
                  <span className={classes.currencySymbol}>₹</span>
                  <input
                    type="text"
                    className={clsx(
                      classes.tokenInput,
                      ((tokenAmountTemp === 0) || (tokenAmountTemp >= installments?.[0]?.amount/100))
                        ? classes.validToken
                        : classes.invalidToken
                    )}
                    value={(tokenAmountTemp) || ''}
                    onChange={this.handleTokenAmountChange}
                  />
                </div>              
                {errorMessage ? (
                  <div className={classes.errorText}>
                    {errorMessage}
                  </div>
                ) : ((tokenAmountTemp < sellPriceTemp) && (tokenAmountTemp >= installments?.[0]?.amount/100)) ? (
                  <div className={classes.remainingText}>
                    Remaining amount of{' '}
                    <strong>
                      {this.formatPrice(_.round(_.sumBy(installments, 'amount')/100 - tokenAmountTemp))}
                    </strong>
                    {' '}to be paid by{' '}
                    <strong>{getIndiaDate(_.last(installments)?.dueDate)}</strong>
                  </div>
                ) : <div><br /></div>}
                {/* Send Button */}
                <button
                  type="button"
                  className={classes.sendButton}
                  onClick={this.handleSendItinerary}
                  disabled={
                    fetchingInstallments
                    || sendingItinerary
                    || !sellPriceTemp
                    || !tokenAmountTemp
                    || (finalPrice < costPrice)
                    || (tokenAmountTemp > sellPriceTemp)
                    || (!!errorMessage)
                  }
                >
                  {
                    sendingItinerary ? 'Sending...' : (aiItineraryData.itinerarySyncedAt ? 'Resend Itinerary' : 'Send Itinerary')
                  }
                </button>
              </div>
            </div>
          
          </div>
        </div>
      </div>
    );
  }
}

const styles = (theme) => ({
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 9999,
  },
  modal: {
    width: 900,
    maxHeight: '90vh',
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    fontFamily: 'Sofia-pro, sans-serif',
  },
  header: {
    height: 56,
    borderBottom: '1px solid #DDDDDD',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 40px',
    flexShrink: 0,
  },
  headerTitle: {
    display: 'flex',
    alignItems: 'center',    
    fontSize: 18,
    fontWeight: 'bold',
    color: theme.colors.textDark,
  },
  headerIcon: {
    marginTop: 2,
    color: '#0090B5',
    marginRight: 10,
  },
  closeButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 8,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.05)',
    },
  },
  content: {
    flex: 1,
    padding: '40px',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    overflowY: 'auto',
  },
  costPriceBox: {
    display: 'flex',
    gap: '40px',
    paddingBottom: '20px',    
    borderBottom: `1px solid ${theme.colors.border}`,
  },
  costPricePrimary: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  costPriceSecondary: {
    padding: '20px',
    display: 'flex',
    backgroundColor: '#EA580C1A',
    borderRadius: 10,
    color: '#EA580C',
  },
  summary: {
    fontSize: 14,
    color: '#EA580C',
  },
  costPriceLabel: {
    fontSize: 16,
    fontWeight: 600,
    color: theme.colors.textLight,
  },
  costPriceValue: {
    fontSize: 24,
    fontWeight: 700,
    color: theme.colors.textDark,
  },
  costDescription: {
    fontSize: 14,
    lineHeight: 1.5,
    color: theme.colors.textLight,
  },
  sellPriceBox: {        
  },
  gridColumn: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  sectionLabel: {
    fontSize: 16,
    fontWeight: 600,
    color: '#322F2C',
  },
  inputContainer: {
    position: 'relative',
    width: '100%',
  },
  priceInput: {
    width: '100%',
    height: 40,
    border: '1px solid',
    borderRadius: 5,
    padding: '12px 16px 12px 40px',
    fontSize: 16,
    color: '#322F2C',
    fontFamily: 'Sofia-pro, sans-serif',
    '&:focus': {
      outline: 'none',
      borderColor: '#0090B5',
    },
    '&::placeholder': {
      color: '#DDDDDD',
    },
  },
  priceInputError: {
    borderColor: theme.colors.red,
    '&:focus': {
      borderColor: theme.colors.red,
    },
  },
  currencySymbol: {
    position: 'absolute',
    left: 16,
    top: '50%',
    transform: 'translateY(-50%)',
    fontSize: 14,
    color: '#322F2C',
    pointerEvents: 'none',
  },
  suggestedPrice: {
    fontSize: 14,
    marginTop: 5,
    color: '#666666',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: '2px',
  },
  infoIcon: {
    marginTop: 2,
    fontSize: 12,
    marginRight: 2,
  },
  offerSection: {
    backgroundColor: 'rgba(44, 122, 119, 0.1)',
    borderRadius: 8,
    height: 48,
    padding: '16px',
    display: 'flex',
    flexDirection: 'row',
    gap: '20px',
    marginTop: 20,
    marginBottom: 20,
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  offerIcon: {
    fontSize: 24,
  },
  offerText: {
    fontSize: 14,
    color: theme.colors.cyan,
    lineHeight: 1.5,
  },
  createOfferButton: {
    backgroundColor: 'transparent',
    color: theme.colors.cyan,
    border: `1px solid ${theme.colors.cyan}`,
    borderRadius: 20,
    height: 32,
    padding: '0 24px',
    fontSize: 14,
    fontWeight: 'bold',
    cursor: 'pointer',
    fontFamily: 'Sofia-pro, sans-serif',    
  },
  displaySection: {
    display: 'flex',
    flexDirection: 'column',    
    width: '20%',
  },
  displayLabel: {
    fontSize: 16,
    fontWeight: 600,
    color: theme.colors.textLight,
    marginBottom: 10,
  },
  displayValue: {
    fontSize: 24,
    fontWeight: 700,
    color: theme.colors.textDark,
  },
  earningsValue: {
    fontSize: 24,
    fontWeight: 700,
    color: '#43A047',
  },
  divider: {
    height: 1,
    backgroundColor: '#DDDDDD',    
  },
  tokenAmountSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  tokenAmountRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '20px',
  },
  tokenHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '24px',
  },
  tokenInputContainer: {
    position: 'relative',
    width: 120,
  },
  tokenInput: {
    width: '100%',
    height: 42,
    border: '1px solid #DDDDDD',
    borderRadius: 4,
    padding: '10px 16px 10px 40px',
    fontSize: 16,
    color: '#43A047',
    fontWeight: 600,
    fontFamily: 'Sofia-pro, sans-serif',
    '&:focus': {
      outline: 'none',
      borderColor: '#0090B5',
    },
  },
  validToken: {
    color: '#43A047',
  },
  invalidToken: {
    color: '#F44336',
  },
  remainingText: {
    width: 250,
    fontSize: 14,
    color: '#666666',
    lineHeight: 1.5,
  },
  errorText: {
    width: 250,
    fontSize: 14,
    color: '#F44336',
    lineHeight: 1.5,
    fontWeight: 500,
  },
  sendButton: {
    width: 300,
    backgroundColor: '#0090B5',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: 24,
    height: 36,
    padding: '0 40px',
    fontSize: 16,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'Sofia-pro, sans-serif',
    marginLeft: 'auto',
    '&:hover': {
      backgroundColor: '#007A94',
    },
    '&:disabled': {
      backgroundColor: '#DDDDDD',
      cursor: 'not-allowed',
    },
  },
  editPricingButton: {
    width: 120,
    height: 34,
    marginTop: 15,
    border: '1px solid #EA580C',
    color: '#EA580C',
    fontWeight: 'bold',
    borderRadius: 17,
    backgroundColor: 'transparent',
    fontSize: 16,
    cursor: 'pointer',
    marginBottom: 12,
    '&:hover': {
      backgroundColor: 'rgba(234, 88, 12, 0.3)',      
    },
  },
  warningIcon: {
    color: '#EA580C',
    marginRight: 10,
  },
});

SellPriceModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  closePricingModal: PropTypes.func.isRequired,
  costPrice: PropTypes.number,
  onSellingPriceSet: PropTypes.func,
  initialSellingPrice: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  classes: PropTypes.object.isRequired,
  aiItineraryData: PropTypes.object.isRequired,
  openPricingModal: PropTypes.func,
  updateItineraryData: PropTypes.func,
  showSnackbar: PropTypes.func,
  itineraryParts: PropTypes.array,
  itineraryId: PropTypes.string,
};

export default withStyles(styles)(SellPriceModal);
